import { useState, useEffect } from 'react';
import { api } from '@/App';
import Layout from '@/components/Layout';
import ShareButtons from '@/components/ShareButtons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Plus, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Opportunities = ({ user, onLogout }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [newOpportunity, setNewOpportunity] = useState({
    title: '',
    description: '',
    category: 'volunteer',
    contact_email: user.email
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const response = await api.get('/opportunities');
      setOpportunities(response.data);
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOpportunity = async (e) => {
    e.preventDefault();
    try {
      await api.post('/opportunities', newOpportunity);
      toast.success('Opportunity posted successfully!');
      setNewOpportunity({
        title: '',
        description: '',
        category: 'volunteer',
        contact_email: user.email
      });
      setDialogOpen(false);
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create opportunity');
    }
  };

  const handleDeleteOpportunity = async (id) => {
    if (window.confirm('Are you sure you want to delete this opportunity?')) {
      try {
        await api.delete(`/opportunities/${id}`);
        toast.success('Opportunity deleted');
        fetchOpportunities();
      } catch (error) {
        toast.error('Failed to delete opportunity');
      }
    }
  };

  const categoryColors = {
    volunteer: 'bg-blue-100 text-blue-700',
    resource: 'bg-purple-100 text-purple-700',
    partnership: 'bg-orange-100 text-orange-700',
    funding: 'bg-green-100 text-green-700'
  };

  const categoryLabels = {
    volunteer: 'Volunteer',
    resource: 'Resource',
    partnership: 'Partnership',
    funding: 'Funding'
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="opportunities-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Opportunities Board</h1>
            <p className="text-gray-600">Share and discover opportunities for collaboration</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="create-opportunity-button">
                <Plus className="h-4 w-4 mr-2" />
                Post Opportunity
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Post New Opportunity</DialogTitle>
                <DialogDescription>Share an opportunity with the community</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateOpportunity} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="Opportunity title"
                    value={newOpportunity.title}
                    onChange={(e) => setNewOpportunity({ ...newOpportunity, title: e.target.value })}
                    required
                    data-testid="opportunity-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={newOpportunity.category}
                    onValueChange={(value) => setNewOpportunity({ ...newOpportunity, category: value })}
                  >
                    <SelectTrigger data-testid="opportunity-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="volunteer">Volunteer</SelectItem>
                      <SelectItem value="resource">Resource</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="funding">Funding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the opportunity..."
                    value={newOpportunity.description}
                    onChange={(e) => setNewOpportunity({ ...newOpportunity, description: e.target.value })}
                    rows={6}
                    required
                    data-testid="opportunity-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="contact@example.com"
                    value={newOpportunity.contact_email}
                    onChange={(e) => setNewOpportunity({ ...newOpportunity, contact_email: e.target.value })}
                    required
                    data-testid="opportunity-email-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="opportunity-submit-button">
                    Post Opportunity
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Opportunities Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading opportunities...</p>
          </div>
        ) : opportunities.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {opportunities.map((opp) => (
              <Card key={opp.id} className="hover:shadow-lg transition-all duration-300 flex flex-col" data-testid={`opportunity-card-${opp.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge className={categoryColors[opp.category]}>
                      {categoryLabels[opp.category]}
                    </Badge>
                    {opp.user_id === user.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteOpportunity(opp.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-opportunity-${opp.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <CardTitle className="text-xl">{opp.title}</CardTitle>
                  <CardDescription className="text-sm">
                    Posted by <span className="font-medium">{opp.org_name}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-700 text-sm mb-4 flex-1">{opp.description}</p>
                  <div className="space-y-3 pt-4 border-t">
                    <p className="text-xs text-gray-500">
                      Posted: {new Date(opp.created_at).toLocaleDateString()}
                    </p>
                    
                    {/* Share Buttons */}
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-2">Share this opportunity:</p>
                      <ShareButtons
                        title={`${opp.title} - CEC Connect`}
                        description={`${opp.description.substring(0, 100)}... Posted by ${opp.org_name}`}
                        url={window.location.href}
                      />
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      asChild
                    >
                      <a href={`mailto:${opp.contact_email}`} data-testid={`contact-opportunity-${opp.id}`}>
                        <Mail className="h-4 w-4 mr-2" />
                        Contact
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No opportunities posted yet.</p>
              <p className="text-gray-400 text-sm mt-2">Be the first to share an opportunity!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Opportunities;